import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { MessageService } from 'primeng/api';
import { environment } from '../../../environments/environment';

export interface SessionLog {
  id: string;
  sessionId: string;
  zoomSessionId?: string;
  eventType: string;
  severity: string;
  source: string;
  userIdentity?: string;
  userRole?: number;
  browser?: string;
  browserVersion?: string;
  os?: string;
  device?: string;
  videoBitrate?: number;
  audioBitrate?: number;
  resolution?: string;
  framerate?: number;
  latency?: number;
  packetLoss?: number;
  jitter?: number;
  cpuUsage?: number;
  bandwidth?: number;
  codec?: string;
  networkQuality?: string;
  connectionType?: string;
  cameraPermission?: boolean;
  microphonePermission?: boolean;
  screenSharePermission?: boolean;
  errorCode?: string;
  errorMessage?: string;
  metadata?: any;
  isIssue: boolean;
  issueCategory?: string;
  timestamp: string;
}

export interface SessionIssue {
  issueCategory: string;
  count: number;
  firstOccurrence: string;
  lastOccurrence: string;
  severity: string;
}

@Component({
  selector: 'app-session-logs-dialog',
  templateUrl: './session-logs-dialog.component.html',
  styleUrls: ['./session-logs-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    TableModule,
    TagModule,
    SelectButtonModule,
    MultiSelectModule,
    TooltipModule,
    ScrollPanelModule
  ]
})
export class SessionLogsDialogComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() sessionId: string | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  logs: SessionLog[] = [];
  filteredLogs: SessionLog[] = [];
  issues: SessionIssue[] = [];
  loading: boolean = false;
  expandedRowKeys: { [key: string]: boolean } = {};

  // Filter options
  showIssuesOnly: boolean = false;
  selectedSources: string[] = [];
  selectedSeverities: string[] = [];
  selectedEventTypes: string[] = [];
  selectedUserIdentities: string[] = [];

  sourceOptions = [
    { label: 'Client', value: 'client' },
    { label: 'Webhook', value: 'webhook' }
  ];

  severityOptions = [
    { label: 'Info', value: 'info' },
    { label: 'Warning', value: 'warning' },
    { label: 'Error', value: 'error' },
    { label: 'Critical', value: 'critical' }
  ];

  eventTypeOptions: any[] = [];
  userIdentityOptions: any[] = [];

  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.sessionId) {
      this.loadLogs();
      this.loadIssues();
    }
  }

  async loadLogs(): Promise<void> {
    if (!this.sessionId) return;

    this.loading = true;
    try {
      this.logs = await firstValueFrom(
        this.http.get<SessionLog[]>(`${this.apiUrl}/zoom/logs/${this.sessionId}`)
      );

      // Extract unique event types and user identities for filters
      this.eventTypeOptions = [...new Set(this.logs.map(log => log.eventType))]
        .map(type => ({ label: this.formatEventType(type), value: type }));

      this.userIdentityOptions = [...new Set(this.logs.map(log => log.userIdentity).filter(Boolean))]
        .map(identity => ({ label: identity, value: identity }));

      this.applyFilters();
    } catch (error) {
      console.error('Error loading session logs:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load session logs'
      });
    } finally {
      this.loading = false;
    }
  }

  async loadIssues(): Promise<void> {
    if (!this.sessionId) return;

    try {
      this.issues = await firstValueFrom(
        this.http.get<SessionIssue[]>(`${this.apiUrl}/zoom/logs/${this.sessionId}/issues`)
      );
    } catch (error) {
      console.error('Error loading session issues:', error);
    }
  }

  applyFilters(): void {
    this.filteredLogs = this.logs.filter(log => {
      if (this.showIssuesOnly && !log.isIssue) return false;
      if (this.selectedSources.length && !this.selectedSources.includes(log.source)) return false;
      if (this.selectedSeverities.length && !this.selectedSeverities.includes(log.severity)) return false;
      if (this.selectedEventTypes.length && !this.selectedEventTypes.includes(log.eventType)) return false;
      if (this.selectedUserIdentities.length && !this.selectedUserIdentities.includes(log.userIdentity || '')) return false;
      return true;
    });
  }

  clearFilters(): void {
    this.showIssuesOnly = false;
    this.selectedSources = [];
    this.selectedSeverities = [];
    this.selectedEventTypes = [];
    this.selectedUserIdentities = [];
    this.applyFilters();
  }

  getSeverityClass(severity: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const severityMap: { [key: string]: 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' } = {
      'info': 'info',
      'warning': 'warn',
      'error': 'danger',
      'critical': 'danger'
    };
    return severityMap[severity] || 'info';
  }

  getSourceIcon(source: string): string {
    return source === 'client' ? 'pi pi-desktop' : 'pi pi-server';
  }

  formatEventType(eventType: string): string {
    return eventType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }

  exportLogs(): void {
    const dataStr = JSON.stringify(this.filteredLogs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `session-logs-${this.sessionId}-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);

    this.messageService.add({
      severity: 'success',
      summary: 'Export Complete',
      detail: 'Session logs exported successfully'
    });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
